import { stripHtmlTags } from "@/utils/stripHtmlTags";
import { API } from "@/api/apiEndPoints";

export const MAX_CHECKPOINT_QUIZ_QUESTIONS = 5;

/**
 * Full POST URL for IQ /suggest-quiz/.
 * @param {string} iqBaseUrl
 * @returns {string}
 */
export function buildIqSuggestQuizEndpoint(iqBaseUrl) {
  return `${String(iqBaseUrl || "")
    .trim()
    .replace(/\/$/, "")}/${API.SUGGEST_QUIZ}`;
}

/**
 * Build POST body for /suggest-quiz/ (SuggestQuizBody).
 * @param {Object} params
 * @param {string} params.checkpointCriteria
 * @param {string} params.microlearningContent
 * @param {string[]} [params.existingQuestions]
 * @param {number} params.remainingSlots
 * @returns {Object}
 */
export function buildSuggestQuizPayload({
  checkpointCriteria,
  microlearningContent,
  existingQuestions = [],
  remainingSlots,
}) {
  return {
    checkpoint_criteria: String(checkpointCriteria || "").trim(),
    microlearning_content: String(microlearningContent || "").trim(),
    existing_questions: Array.isArray(existingQuestions)
      ? existingQuestions.map((q) => String(q || "").trim()).filter(Boolean)
      : [],
    remaining_slots: remainingSlots,
  };
}

/**
 * Extract plain question texts from quiz rows for duplicate avoidance.
 * @param {Array<Object>} questions
 * @returns {string[]}
 */
export function extractExistingQuizQuestionTexts(questions) {
  if (!Array.isArray(questions)) return [];
  return questions
    .map((row) => String(row?.q_text ?? row?.question ?? row?.text ?? "").trim())
    .filter(Boolean);
}

function appendTrimmedPart(parts, value) {
  const text = String(value ?? "").trim();
  if (text) parts.push(text);
}

/**
 * Build microlearning text for video checkpoints from TOC / summary payload.
 * Also accepts migrated inspection-style payloads (`steps` + pass/fail `summary`).
 * @param {Object|null|undefined} payload
 * @returns {string}
 */
export function buildVideoMicrolearningContentForSuggest(payload) {
  if (!payload || typeof payload !== "object") return "";

  const parts = [];
  appendTrimmedPart(parts, payload.video_title);

  const summary = payload.microlearning_summary ?? payload.summary;
  if (typeof summary === "string" && summary.trim()) {
    parts.push(summary.trim());
  } else if (summary && typeof summary === "object" && !Array.isArray(summary)) {
    const overview = summary.overview ?? summary.text ?? "";
    if (String(overview).trim()) {
      parts.push(String(overview).trim());
    } else if (typeof summary.summary_lead === "string" || Array.isArray(summary.summary_bullets)) {
      // Two-part VIDEO_WITH_TOC summary: lead sentence + ordered bullet list.
      appendTrimmedPart(parts, summary.summary_lead);
      if (Array.isArray(summary.summary_bullets)) {
        for (const bullet of summary.summary_bullets) {
          appendTrimmedPart(parts, bullet);
        }
      }
    } else {
      appendTrimmedPart(parts, summary.pass);
      appendTrimmedPart(parts, summary.fail);
      appendTrimmedPart(parts, summary.borderline);
    }
  }

  const chapters = payload?.table_of_contents?.chapters ?? payload?.chapters ?? [];
  if (Array.isArray(chapters)) {
    for (const chapter of chapters) {
      const title = chapter?.chapter_title ?? chapter?.title ?? "";
      const description =
        chapter?.chapter_description ?? chapter?.chapter_summary ?? chapter?.description ?? "";
      const segment = [title, description].filter((part) => String(part || "").trim()).join(": ");
      if (segment.trim()) {
        parts.push(segment.trim());
      }
    }
  }

  const steps = payload?.steps ?? payload?.inspection_instructions?.steps ?? [];
  if (Array.isArray(steps) && (!Array.isArray(chapters) || chapters.length === 0)) {
    for (const step of steps) {
      const title = step?.look_for ?? step?.inspector_action ?? step?.chapter_title ?? "";
      const description = [step?.pass_criteria, step?.fail_criteria, step?.inspector_action]
        .filter((part) => String(part || "").trim())
        .join(" ");
      const segment = [title, description].filter((part) => String(part || "").trim()).join(": ");
      if (segment.trim()) {
        parts.push(segment.trim());
      }
    }
  }

  return parts.join("\n\n").trim();
}

/**
 * Build microlearning text for article checkpoints.
 * @param {Object} params
 * @param {string|null|undefined} params.articleHtml
 * @param {Object|null|undefined} params.articleBody
 * @returns {string}
 */
export function buildArticleMicrolearningContentForSuggest({ articleHtml, articleBody }) {
  if (typeof articleHtml === "string" && articleHtml.trim()) {
    const plain = stripHtmlTags(articleHtml).replace(/\s+/g, " ").trim();
    if (plain) return plain;
  }

  const bodyText = extractPlainTextFromTipTapDoc(articleBody);
  if (bodyText) return bodyText;

  return "";
}

function extractPlainTextFromTipTapDoc(node) {
  if (node == null) return "";
  if (typeof node === "string") return node.trim();
  if (typeof node !== "object") return "";

  if (typeof node.text === "string" && node.text.trim()) {
    return node.text.trim();
  }

  const content = Array.isArray(node.content) ? node.content : [];
  const chunks = content.map((child) => extractPlainTextFromTipTapDoc(child)).filter(Boolean);
  return chunks.join(" ").replace(/\s+/g, " ").trim();
}

/**
 * Map IQ /suggest-quiz/ questions to checkpoint proposed_quiz API rows.
 * @param {Array<Object>} questions
 * @param {number} [startQNo]
 * @returns {Array<Object>}
 */
export function mapSuggestedQuizQuestionsToApi(questions, startQNo = 1) {
  if (!Array.isArray(questions)) return [];

  return questions
    .map((question, index) => {
      const q_text = String(question?.question_text ?? question?.q_text ?? "").trim();
      const rawOptions = Array.isArray(question?.options)
        ? question.options
        : Array.isArray(question?.q_answers)
        ? question.q_answers
        : [];

      const q_answers = rawOptions
        .map((option, optionIndex) => {
          const a_text = String(
            option?.option_text ?? option?.a_text ?? option?.text ?? option?.answer ?? ""
          ).trim();
          if (!a_text) return null;

          const isCorrectFlag =
            option?.is_correct === true ||
            option?.isCorrect === 1 ||
            option?.isCorrect === true ||
            Number(option?.isCorrect) === 1;
          const isCorrect = isCorrectFlag ? 1 : 0;

          return {
            a_no: optionIndex + 1,
            a_text,
            isCorrect,
            correct_description:
              isCorrect === 1 ? String(option?.correct_description ?? "").trim() : "",
          };
        })
        .filter(Boolean);

      if (!q_text || q_answers.length === 0) return null;

      return {
        q_no: startQNo + index,
        q_text,
        q_answers,
      };
    })
    .filter(Boolean);
}
